import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building, Users, FileText, Workflow, Stethoscope, Target, ScrollText, CreditCard } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export function PlatformOverviewPage() {
  const metrics = useQuery({
    queryKey: ['platform-admin', 'metrics'],
    queryFn: async () => (await api.get('/api/platform-admin/metrics')).data,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Platform overview</h1>
        <p className="text-sm text-muted-foreground">
          Cross-tenant metrics for the Praxara operator team.
        </p>
      </div>

      {metrics.isLoading ? (
        <div className="grid gap-3 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-4">
            <Tile icon={Building} label="Tenants" value={metrics.data.totals.tenants} to="/platform-admin/tenants" />
            <Tile icon={Users} label="Users" value={metrics.data.totals.users} to="/platform-admin/users" />
            <Tile icon={CreditCard} label="Active subscriptions" value={metrics.data.totals.activeSubscriptions} to="/platform-admin/subscriptions" />
            <Tile icon={ScrollText} label="Audit entries" value={metrics.data.totals.auditLogs} to="/platform-admin/audit" />
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            <Tile icon={FileText} label="Documents" value={metrics.data.totals.documents} tone="info" />
            <Tile icon={Workflow} label="Workflow runs" value={metrics.data.totals.workflowRuns} tone="info" />
            <Tile icon={Stethoscope} label="PV cases" value={metrics.data.totals.pvCases} tone="info" />
            <Tile icon={Target} label="Compliance scans" value={metrics.data.totals.complianceScans} tone="info" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Last 30 days</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <MiniStat label="New tenants" value={metrics.data.last30Days.newTenants} />
                <MiniStat label="New users" value={metrics.data.last30Days.newUsers} />
                <MiniStat label="Workflow runs" value={metrics.data.last30Days.workflowRuns} />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  to,
  tone = 'primary',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  to?: string;
  tone?: 'primary' | 'info';
}) {
  const body = (
    <Card className={cn('transition-all', to && 'hover:shadow-md hover:border-primary/30')}>
      <CardContent className="flex items-start justify-between p-4">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </div>
          <div className="mt-1 font-display text-2xl">{value.toLocaleString()}</div>
        </div>
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-md', tone === 'primary' ? 'bg-primary/10 text-primary' : 'bg-blue-500/10 text-blue-600')}>
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-muted/30 p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl">{value.toLocaleString()}</div>
    </div>
  );
}
